/*
 * -----------------------------------------------------------------------------
 *  Lucom Interaction Platform
 *  (C) Lucom GmbH, Erkrath. All rights reserved.
 * -----------------------------------------------------------------------------
 */

import { baseComponents, configuration as sharedConfiguration } from "./shared/data";
import type {BaseComponents, Configuration, RemoteModule} from "./shared/types";
import Logger, { initialize as initializeLogging } from "./utils/Logger";
import { getDocument } from "./utils/EmbeddedEnvironment";
import Network from "./utils/Network";
import { EventBus } from "light-event-bus";
import SessionStorage from "./utils/SessionStorage";
import SessionSync from "./utils/SessionSync";

declare global {
    interface Window {
        lucom: any;
        LIP: any;
    }
    interface Document {
        WCI_LOADING: any;
    }
}

const log = new Logger(import.meta.url);
window.lucom = window.lucom || {};
window.lucom.ip = window.lucom.ip || {};

const configuration:Configuration = window.lucom.ip.configuration;
sharedConfiguration.set(configuration);
window.lucom.ip.configuration = configuration;

const refresh = configuration.refresh || false;

initializeLogging(configuration);

if (log.debugEnabled) {
    log.debug("bootstrapping");
    log.debug(`configuration = ${JSON.stringify(configuration)}`);
    log.debug(`refresh = ${refresh}`);
}

if (refresh) {
    // copy previous baseComponents into new (empty) baseComponents container
    window.dispatchEvent(new CustomEvent("lucom.ip.shell.getSharedComponent", {
        composed: true,
        detail: {
            callback: function (components : BaseComponents) {
                Object.keys (components).forEach (function (name) {
                    baseComponents[name] = components[name];
                });
            }
        }
    }));

} else {
    baseComponents.network = Network.create(configuration.baseURL, configuration.ajaxRequestHeaders);
    baseComponents.eventBus = new EventBus();
    baseComponents.sessionStorage = new SessionStorage ("shell");
    baseComponents.sessionSync = new SessionSync (baseComponents.network, baseComponents.eventBus);

    window.addEventListener("lucom.ip.shell.registerModules", (event) => {
        const remoteModules: RemoteModule[] = (<any>event).detail?.remoteModules || [];

        if (remoteModules.length > 0) {
            if (log.debugEnabled) log.debug(`initialization of ${remoteModules.length} additional modules requested`);

            // load and initialize remote modules
            initializeModules(remoteModules);
        }
    });

    window.addEventListener("lucom.ip.shell.unregisterModules", (event) => {
        const remoteModules: RemoteModule[] = (<any>event).detail?.remoteModules || [];

        if (remoteModules.length > 0) {
            if (log.debugEnabled) log.debug(`de-initialization of ${remoteModules.length} modules requested`);

            // de-initialize remote modules
            deInitializeModules(remoteModules);
        }
    });
}

// invalidate local session storage when not in sync with server session any longer
const storedConversation = baseComponents.sessionStorage.get ("conversation");
if (!storedConversation || configuration.conversation !== storedConversation) {
    if (log.debugEnabled) log.debug("invalidate session storage upon new conversation");
    SessionStorage.clearAll();
}
baseComponents.sessionStorage.update("conversation", configuration.conversation);

// start session prolongation timer, not applicable for standalone use
if (configuration.sessionProlongation && configuration.embedded) {
    if (log.debugEnabled) log.debug("activate session prolongation");
    baseComponents.sessionSync.keepSessionAlive(configuration.sessionProlongation);
} else {
    if (log.debugEnabled) log.debug("no session prolongation");
}

let formHelpModule: any;
let formNavigationModule: any;
let formViewModule: any;
let formCatalogModule: any;
let approvalProcessCockpitModule: any;
let approvalProcessEditorModule: any;
let submitterCommunicationCockpitModule: any;
async function initializeModules(remoteModules: RemoteModule[]) {
    await Promise.all(
        remoteModules.map(async (remoteModule) => {
            if (!remoteModule.exposedModule) {
                return;
            }

            const context = {
                configuration,
                getDocument,
            };

            //The following if-statements with individual imports are necessary
            //for Vite to pick up the async imported modules. It wouldn't work
            //if the import statement would contain a variable.
            if (remoteModule.exposedModule == "FormHelp") {
                formHelpModule = await import("./modules/FormHelp/module");
                formHelpModule.default.initialize (context);
            }

            if (remoteModule.exposedModule == "FormNavigation") {
                formNavigationModule = await import("./modules/FormNavigation/module");
                formNavigationModule.default.initialize (context);
            }

            if (remoteModule.exposedModule == "FormView") {
                formViewModule = await import("./modules/FormView/module");
                formViewModule.default.initialize (context);
            }

            if (remoteModule.exposedModule == "FormCatalog") {
                formCatalogModule = await import("./modules/FormCatalog/module");
                formCatalogModule.default.initialize (context);
            }

            if (remoteModule.exposedModule == "ApprovalProcessCockpit") {
                approvalProcessCockpitModule = await import("./modules/ApprovalProcessCockpit/module");
                approvalProcessCockpitModule.default.initialize (context);
            }

            if (remoteModule.exposedModule == "ApprovalProcessEditor") {
                approvalProcessEditorModule = await import("./modules/ApprovalProcessEditor/module");
                approvalProcessEditorModule.default.initialize (context);
            }

            if (remoteModule.exposedModule == "SubmitterCommunicationCockpit") {
                submitterCommunicationCockpitModule = await import("./modules/SubmitterCommunicationCockpit/module");
                submitterCommunicationCockpitModule.default.initialize (context);
            }
        })
    );
}

async function deInitializeModules(remoteModules: RemoteModule[]) {
    await Promise.all(
        remoteModules.map(async (remoteModule) => {
            if (!remoteModule.exposedModule) {
                return;
            }

            if (remoteModule.exposedModule == "FormNavigation") {
                if (formNavigationModule)
                    formNavigationModule.default.deInitialize ();
            }
        })
    );
}

if (refresh) {
    baseComponents.eventBus.publish ("lucom.ip.shell.refreshed");

} else {
    // allow letting the session timeout, e.g. through the hosting portal
    baseComponents.eventBus.subscribe("shell.command.stopSessionProlongation", () => {
        if (log.debugEnabled) log.debug("stopping session prolongation");
        baseComponents.sessionSync.letSessionTimeout();
    });

    // allow terminating the session immediately, closing all forms, through the hosting portal
    baseComponents.eventBus.subscribe("shell.command.terminateSession", () => {
        baseComponents.sessionSync.terminateSession().then(() => {
            log.debug("session terminated!");
            SessionStorage.clearAll();
        });
    });

    // get information about an existing session, not implicitly creating one when missing.
    // the callback function provided by the event publisher has the signature: function (sessionInfoPromise) -> void
    // it is invoked after obtaining the session information from the server (async), resolving to a structure with
    // session information or to undefined (when no session is available), rejecting in case of communication error
    baseComponents.eventBus.subscribe("shell.command.getSessionInfo", (callback) => {
        if (typeof callback === "function") callback (baseComponents.sessionSync.getSessionInfo());
        else throw new Error("invalid callback: " + callback);
    });

    // allow switching the locale via eventBus, e.g. through the hosting portal
    baseComponents.eventBus.subscribe("shell.command.setLocale", (locale) => {
        if (log.debugEnabled) log.debug(`setting locale to ${locale}`);

        const lastLocale = baseComponents.sessionStorage.get("locale");
        baseComponents.sessionStorage.update("locale", locale);
        configuration.locale = locale;
        baseComponents.sessionSync.setLocale(locale).then((locale: string) => {
            baseComponents.eventBus.publish("shell.localeSet", locale);
            if (locale !== lastLocale) {
                baseComponents.eventBus.publish("shell.localeChanged", { locale, lastLocale });
            }
        });
    });

    // Allow consumers to request the configuration by dispatching an event with a callback function in the payload.
    // The callback function is invoked with the configuration.
    // Note that this also supports the case where multiple LIP instances are embedded in a single portal page.
    // Every instance registers its own event handler, so the consumer's callback function is called several times.
    window.addEventListener("lucom.ip.shell.requestConfiguration", (event: any) => {
        if (log.debugEnabled) log.debug("expose requested configuration");

        const callback = (event.detail && event.detail.callback) || undefined;
        if (typeof callback !== "function") log.error(`invalid callback function: ${callback}`);
        else callback (configuration);
    });

    // Allow consumers to request an internal shared component by name like the EventBus, the Vue runtime and so on.
    // internal API, available base components may change over time.
    // The callback function is invoked.
    window.addEventListener("lucom.ip.shell.getSharedComponent", (event: any) => {
        const name: keyof typeof baseComponents = (event.detail && event.detail.name) || undefined;
        if (!name) log.error(`missing name!`);

        if (log.debugEnabled) {
            if (typeof name === "undefined")
                log.debug (`get shared component ${name}`);
            else
                log.debug (`get shared components`);
        }

        const callback = (event.detail && event.detail.callback) || undefined;
        if (typeof callback !== "function")
            log.error(`invalid callback function: ${callback}`);
        else if (typeof name === "undefined")
            callback (baseComponents);
        else {
            const baseComponent = baseComponents[name];
            if (!baseComponent) log.error(`no such shared component: ${name}`);
            else callback (baseComponent);
        }
    });

    // Allow consumers to register an internal shared component.
    // internal API!
    window.addEventListener("lucom.ip.shell.defineSharedComponent", (event: any) => {
        const name: keyof typeof baseComponents = (event.detail && event.detail.name) || undefined;
        if (!name) log.error(`missing name!`);

        const component = (event.detail && event.detail.component) || undefined;

        const defined = typeof component !== "undefined";
        if (log.debugEnabled) log.debug(`${defined ? "define" : "undefine"} shared component ${name}`);

        if (defined) baseComponents[name] = component;
        else delete baseComponents[name];
    });

    window.dispatchEvent(
        new CustomEvent("lucom.initialized", {
            composed: true,
            detail: { eventBus: baseComponents.eventBus },
        })
    );

    window.dispatchEvent(
        new CustomEvent("lucom.wci.initialized", {
            composed: true,
            detail: { eventBus: baseComponents.eventBus },
        })
    );

    window.lucom.ip.shell = window.lucom.ip.shell || {};
    window.lucom.ip.configuration = window.lucom.ip.configuration || {};
    window.lucom.ip.shell.initialized = true;
    if (window.lucom.ip.configuration.remoteModules) {
        const remoteModules = window.lucom.ip.configuration.remoteModules;
        if (log.debugEnabled) log.debug(`initialization of ${remoteModules.length} modules requested`);

        // load and initialize remote modules
        initializeModules(remoteModules);
    }
 }
